import { useState, useEffect } from 'react';
import { 
  Plus, Lock, Cloud, AlertCircle, Shield, Unlock, 
  BookmarkIcon, FileText, Edit3, Trash2, X, Search, Tags
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useVaults } from '../hooks/useVaults';
import NavigationHeader from '../components/NavigationHeader';
import { Button, Input, Textarea, Card } from '@safermark/shared/ui';
import type { BookmarkItem, NoteItem } from '@safermark/shared/types';

interface UnlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUnlock: (password: string) => Promise<void>;
}

function UnlockModal({ isOpen, onClose, onUnlock }: UnlockModalProps) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;

    try {
      setLoading(true);
      setError('');
      await onUnlock(password);
      setPassword('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlock vault');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Unlock Cloud Vault</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="password"
            placeholder="Enter vault password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(e as any);
              }
            }}
            autoFocus
          />
          
          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              loading={loading}
              disabled={!password.trim()}
            >
              <Unlock size={16} />
              Unlock
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface CreateVaultModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (password: string, name: string) => Promise<void>;
}

function CreateVaultModal({ isOpen, onClose, onCreate }: CreateVaultModalProps) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !password.trim()) return;

    try {
      setLoading(true);
      setError('');
      await onCreate(password, name);
      setName('');
      setPassword('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create vault');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Create New Cloud Vault</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            placeholder="Vault name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(e as any);
              }
            }}
            autoFocus
          />
          
          <Input
            type="password"
            placeholder="Vault password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(e as any);
              }
            }}
          />

          
          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              loading={loading}
              disabled={!name.trim() || !password.trim()}
            >
              <Plus size={16} />
              Create Cloud Vault
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (item: Omit<BookmarkItem, 'id' | 'createdAt' | 'updatedAt'> | Omit<NoteItem, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  type: 'bookmark' | 'note';
}

function AddItemModal({ isOpen, onClose, onAdd, type }: AddItemModalProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const baseItem = {
      type,
      title: title.trim() || 'Untitled',
      tags
    };

    const item = type === 'bookmark' 
      ? { ...baseItem, type: 'bookmark' as const, url: url.trim() }
      : { ...baseItem, type: 'note' as const, content: content.trim() };

    if (type === 'bookmark' && !url.trim()) return;
    if (type === 'note' && !content.trim()) return;

    try {
      setLoading(true);
      setError('');
      await onAdd(item);
      setTitle('');
      setContent('');
      setUrl('');
      setTags([]);
      setTagInput('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to add ${type}`);
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Add {type === 'bookmark' ? 'Bookmark' : 'Note'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            placeholder={`${type === 'bookmark' ? 'Bookmark' : 'Note'} title`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
          
          {type === 'bookmark' && (
            <Input
              type="url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
          )}

          {type === 'note' && (
            <Textarea
              placeholder="Write your note here..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              required
            />
          )}

          <div>
            <div className="flex gap-2 mb-2">
              <Input
                placeholder="Add tag..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
              />
              <Button type="button" onClick={addTag} variant="secondary">
                <Tags size={16} />
              </Button>
            </div>
            
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              loading={loading}
              disabled={type === 'bookmark' ? !url.trim() : !content.trim()}
            >
              <Plus size={16} />
              Add {type === 'bookmark' ? 'Bookmark' : 'Note'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface EditItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: (itemId: string, item: BookmarkItem | NoteItem) => Promise<void>;
  item: BookmarkItem | NoteItem;
}

function EditItemModal({ isOpen, onClose, onEdit, item }: EditItemModalProps) {
  const [title, setTitle] = useState(item.title);
  const [content, setContent] = useState(item.type === 'note' ? item.content : '');
  const [url, setUrl] = useState(item.type === 'bookmark' ? item.url : '');
  const [tags, setTags] = useState<string[]>(item.tags);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validate required fields
    if (item.type === 'bookmark' && !url.trim()) {
      setError('URL is required for bookmarks');
      return;
    }
    if (item.type === 'note' && !content.trim()) {
      setError('Content is required for notes');
      return;
    }

    const baseItem = {
      ...item,
      title: title.trim() || 'Untitled',
      tags,
      updatedAt: Date.now()
    };

    const updatedItem = item.type === 'bookmark' 
      ? { ...baseItem, type: 'bookmark' as const, url: url.trim() }
      : { ...baseItem, type: 'note' as const, content: content.trim() };

    try {
      setLoading(true);
      setError('');
      await onEdit(item.id, updatedItem);
      // Add a small delay before closing to ensure the form submission completes
      setTimeout(() => onClose(), 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to update ${item.type}`);
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Edit {item.type === 'bookmark' ? 'Bookmark' : 'Note'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            placeholder={`${item.type === 'bookmark' ? 'Bookmark' : 'Note'} title`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(e as any);
              }
            }}
            autoFocus
          />
          
          {item.type === 'bookmark' && (
            <Input
              type="url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit(e as any);
                }
              }}
              required
            />
          )}

          {item.type === 'note' && (
            <Textarea
              placeholder="Write your note here..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  e.preventDefault();
                  handleSubmit(e as any);
                }
              }}
              rows={4}
              required
            />
          )}

          <div>
            <div className="flex gap-2 mb-2">
              <Input
                placeholder="Add tag..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
              />
              <Button type="button" onClick={addTag} variant="secondary" size="sm">
                <Tags size={14} />
              </Button>
            </div>
            
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs flex items-center gap-1"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              loading={loading}
              disabled={
                (item.type === 'bookmark' && !url.trim()) ||
                (item.type === 'note' && !content.trim())
              }
            >
              <Edit3 size={16} />
              Update {item.type === 'bookmark' ? 'Bookmark' : 'Note'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function VaultsPage() {
  const { isPremium, refreshPremiumStatus } = useAuth();
  const {
    unlockedVaults,
    cloudVaultMetas,
    error,
    loadCloudVaultMetas,
    unlockVault,
    lockVault,
    createVault,
    deleteVault,
    addBookmark,
    addNote,
    updateItem,
    deleteItem
  } = useVaults();

  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState<{ vaultId: string; type: 'bookmark' | 'note' } | null>(null);
  const [showEditItemModal, setShowEditItemModal] = useState<{ vaultId: string; item: BookmarkItem | NoteItem } | null>(null);
  const [selectedVault, setSelectedVault] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'bookmarks' | 'notes'>('bookmarks');

  useEffect(() => {
    loadCloudVaultMetas();
  }, [loadCloudVaultMetas]);

  // Auto-refresh premium status if user appears non-premium on vault page entry
  useEffect(() => {
    if (!isPremium) {
      console.log('VaultsPage: User appears non-premium, refreshing status to ensure it\'s up-to-date');
      refreshPremiumStatus().catch(error => {
        console.warn('Failed to refresh premium status on vault page entry:', error);
      });
    }
  }, [isPremium, refreshPremiumStatus]);

  const handleUnlock = async (password: string) => {
    const vault = await unlockVault(password);
    setSelectedVault(vault.id);
  };

  const handleCreate = async (password: string, name: string) => {
    const vault = await createVault(password, name);
    setSelectedVault(vault.id);
  };

  const handleAddItem = async (vaultId: string, item: any) => {
    if (item.type === 'bookmark') {
      await addBookmark(vaultId, item);
    } else {
      await addNote(vaultId, item);
    }
  };

  const handleEditItem = async (vaultId: string, itemId: string, updatedItem: BookmarkItem | NoteItem) => {
    await updateItem(vaultId, itemId, updatedItem);
  };

  const selectedVaultData = selectedVault ? unlockedVaults[selectedVault]?.data : null;
  const filteredItems = selectedVaultData ? [
    ...(activeTab === 'bookmarks' ? selectedVaultData.bookmarks : selectedVaultData.notes)
  ].filter(item => {
    const query = searchQuery.toLowerCase();
    return item.title.toLowerCase().includes(query) ||
           item.tags.some((tag: string) => tag.toLowerCase().includes(query)) ||
           (item.type === 'bookmark' && item.url.toLowerCase().includes(query)) ||
           (item.type === 'note' && item.content.toLowerCase().includes(query));
  }) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Vault Manager</h2>
            <p className="text-gray-600">Create, unlock, and manage your encrypted vaults</p>
          </div>
          <div className="flex gap-2">
            {isPremium ? (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus size={16} />
                Create Cloud Vault
              </Button>
            ) : (
              <Button variant="secondary" disabled>
                <Plus size={16} />
                Create Cloud Vault
              </Button>
            )}
            {isPremium ? (
              <Button onClick={() => setShowUnlockModal(true)}>
                <Unlock size={16} />
                Unlock Cloud Vault
              </Button>
            ) : (
              <Button variant="secondary" disabled>
                <Unlock size={16} />
                Unlock Cloud Vault
              </Button>
            )}
            {/*{isPremium && (*/}
            {/*  <Button onClick={() => setShowUnlockModal(true)}>*/}
            {/*    <Cloud size={16} />*/}
            {/*    Unlock Cloud*/}
            {/*  </Button>*/}
            {/*)}*/}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {!isPremium && cloudVaultMetas.length > 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <AlertCircle className="h-8 w-8 text-amber-600 mr-4 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Free User - Limited Web Access
                </h3>
                <p className="text-gray-700 mb-4">
                  Free users cannot unlock or view cloud vaults in the web app. However, you can:
                </p>
                <ul className="text-gray-700 mb-4 list-disc list-inside space-y-1">
                  <li>Use the Chrome extension to download your cloud vaults as local vaults</li>
                  <li>Delete cloud vaults from this page to clear your data</li>
                  <li>Upgrade to Premium for full web vault management</li>
                </ul>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={() => window.location.href = '/subscription'}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Upgrade to Premium
                  </Button>
                  <Button 
                    variant="secondary"
                    onClick={() => window.location.href = '/subscription'}
                  >
                    View Plans
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isPremium && cloudVaultMetas.length === 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <Shield className="h-8 w-8 text-blue-600 mr-4 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Unlock Premium Vault Features
                </h3>
                <p className="text-gray-700 mb-4">
                  Upgrade to Premium to create new vaults, unlock existing vaults, and manage your encrypted bookmarks and notes.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={() => window.location.href = '/subscription'}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Upgrade to Premium
                  </Button>
                  <Button 
                    variant="secondary"
                    onClick={() => window.location.href = '/subscription'}
                  >
                    View Plans
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Vault List Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4">
              <h3 className="font-medium text-gray-900 mb-3">Unlocked Vaults</h3>
              
              {Object.keys(unlockedVaults).length === 0 ? (
                <div className="text-center py-8">
                  <Lock size={32} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">No unlocked vaults</p>
                  <p className="text-xs text-gray-500 mt-1">Create or unlock a vault to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(unlockedVaults).map(([id, vault]) => (
                    <div
                      key={id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedVault === id 
                          ? 'bg-blue-50 border-blue-200' 
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                      onClick={() => setSelectedVault(id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm truncate">
                          {vault.data.name || 'Unnamed Vault'}
                        </span>
                        <div className="flex gap-1">
                          {vault.isCloudVault && <Cloud size={12} className="text-blue-500" />}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              lockVault(id);
                              if (selectedVault === id) setSelectedVault(null);
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Lock size={12} />
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-3 text-xs text-gray-600">
                        <span>{vault.data.bookmarks.length} bookmarks</span>
                        <span>{vault.data.notes.length} notes</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Cloud Vault Metas */}
              {cloudVaultMetas.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Cloud size={16} className="mr-1" />
                    Cloud Vaults
                  </h4>
                  <div className="space-y-2">
                    {cloudVaultMetas.map(vault => {
                      const isUnlocked = unlockedVaults[vault.id];
                      const vaultName = isUnlocked?.data.name;
                      return (
                        <div key={vault.id} className={`p-3 border rounded-lg opacity-50 ${
                          isUnlocked ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
                        }`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                {isUnlocked ? (
                                  <Unlock size={12} className="text-green-600 flex-shrink-0" />
                                ) : (
                                  <Lock size={12} className="text-blue-600 flex-shrink-0" />
                                )}
                                <span className={`text-xs font-medium truncate ${
                                  isUnlocked ? 'text-green-800' : 'text-blue-800'
                                }`}>
                                  {vaultName? `Name: ${vaultName} (${vault.id.slice(-8)})` : `ID: ${vault.id.slice(-8)}`}
                                </span>
                              </div>
                              <div className={`text-xs ${
                                isUnlocked ? 'text-green-600' : 'text-blue-600'
                              }`}>
                                {isUnlocked ? 'Vault Unlocked' : 'Vault Locked'} · {Math.round(vault.size / 1024)} KB
                              </div>
                            </div>
                            <div className="flex gap-1 ml-2">
                              {!isUnlocked && isPremium && (
                                <button
                                  onClick={() => setShowUnlockModal(true)}
                                  className="p-1 text-blue-600 hover:text-blue-800 rounded"
                                  title="Unlock vault"
                                >
                                  <Unlock size={12} />
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to delete this cloud vault? This action cannot be undone.')) {
                                    if (window.confirm('WARNING: All data in this vault will be permanently deleted. This cannot be undone. Are you absolutely sure?')) {
                                      deleteVault(vault.id);
                                    }
                                  }
                                }}
                                className="p-1 text-red-400 hover:text-red-600 rounded"
                                title="Delete vault permanently"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Vault Content */}
          <div className="lg:col-span-3">
            {selectedVaultData ? (
              <Card className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">
                    {selectedVaultData.name || 'Unnamed Vault'}
                  </h3>
                  <div className="flex gap-2">
                    {isPremium ? (
                      <Button
                        onClick={() => setShowAddItemModal({ vaultId: selectedVault!, type: 'bookmark' })}
                        variant="primary"
                        size="sm"
                      >
                        <BookmarkIcon size={16} />
                        Add Bookmark
                      </Button>
                    ) : (
                      <Button variant="secondary" disabled size="sm">
                        <BookmarkIcon size={16} />
                        Add Bookmark
                      </Button>
                    )}
                    {isPremium ? (
                      <Button
                        onClick={() => setShowAddItemModal({ vaultId: selectedVault!, type: 'note' })}
                        variant="primary"
                        size="sm"
                      >
                        <FileText size={16} />
                        Add Note
                      </Button>
                    ) : (
                      <Button variant="secondary" disabled size="sm">
                        <FileText size={16} />
                        Add Note
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this vault?')) {
                          deleteVault(selectedVault!);
                          setSelectedVault(null);
                        }
                      }}
                      variant="danger"
                      size="sm"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>

                <div className="mb-4">
                  <Input
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    icon={Search}
                  />
                </div>

                <div className="flex justify-between items-center mb-4">
                  <div className="flex gap-1">
                    <button
                      onClick={() => setActiveTab('bookmarks')}
                      className={`px-3 py-2 text-sm font-medium rounded-lg ${
                        activeTab === 'bookmarks'
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <BookmarkIcon size={14} className="inline mr-1" />
                      Bookmarks ({selectedVaultData.bookmarks.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('notes')}
                      className={`px-3 py-2 text-sm font-medium rounded-lg ${
                        activeTab === 'notes'
                          ? 'bg-green-100 text-green-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <FileText size={14} className="inline mr-1" />
                      Notes ({selectedVaultData.notes.length})
                    </button>
                  </div>
                  
                  {isPremium && (
                    <Button
                      onClick={() => setShowAddItemModal({ vaultId: selectedVault!, type: activeTab === 'bookmarks' ? 'bookmark' : 'note' })}
                      size="sm"
                      variant="primary"
                    >
                      <Plus size={14} className="mr-1" />
                      Add {activeTab === 'bookmarks' ? 'Bookmark' : 'Note'}
                    </Button>
                  )}
                </div>

                <div className="space-y-3">
                  {filteredItems.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-2">
                        {activeTab === 'bookmarks' ? <BookmarkIcon size={32} /> : <FileText size={32} />}
                      </div>
                      <p className="text-gray-600">No {activeTab} found</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {searchQuery ? 'Try a different search term' : `Add your first ${activeTab.slice(0, -1)}`}
                      </p>
                    </div>
                  ) : (
                    filteredItems.map((item) => (
                      <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0 pr-4">
                            <div className="flex items-center gap-2 mb-2 min-w-0">
                              {item.type === 'bookmark' ? (
                                <BookmarkIcon size={16} className="text-blue-600 flex-shrink-0" />
                              ) : (
                                <FileText size={16} className="text-green-600 flex-shrink-0" />
                              )}
                              <h4 className="font-medium text-gray-900 truncate min-w-0 flex-1">{item.title}</h4>
                            </div>
                            
                            {item.type === 'bookmark' && (
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 mb-2 block truncate w-full"
                                title={item.url}
                              >
                                {item.url}
                              </a>
                            )}

                            {item.type === 'note' && (
                              <p className="text-gray-700 text-sm mb-2 line-clamp-3">
                                {item.content}
                              </p>
                            )}

                            {item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {item.tags.map((tag: string) => (
                                  <span
                                    key={tag}
                                    className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex gap-1 ml-4">
                            {isPremium ? (
                              <button
                                onClick={() => {
                                  setShowEditItemModal({ vaultId: selectedVault!, item });
                                }}
                                className="p-2 text-gray-400 hover:text-gray-600 rounded"
                              >
                                <Edit3 size={16} />
                              </button>
                            ) : (
                              <button
                                disabled
                                className="p-2 text-gray-300 rounded cursor-not-allowed"
                                title="Edit requires Premium"
                              >
                                <Edit3 size={16} />
                              </button>
                            )}
                            {isPremium ? (
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to delete this item?')) {
                                    deleteItem(selectedVault!, item.id);
                                  }
                                }}
                                className="p-2 text-red-400 hover:text-red-600 rounded"
                              >
                                <Trash2 size={16} />
                              </button>
                            ) : (
                              <button
                                disabled
                                className="p-2 text-gray-300 rounded cursor-not-allowed"
                                title="Delete requires Premium"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            ) : (
              <Card className="p-8 text-center">
                <Shield size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Vault</h3>
                <p className="text-gray-600 mb-4">
                  Choose a vault from the sidebar to view and manage its contents.
                </p>
                <p className="text-sm text-gray-500">
                  All encryption and decryption happens in your browser - your passwords never leave your device.
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <UnlockModal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        onUnlock={handleUnlock}
      />

      <CreateVaultModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreate}
      />

      {showAddItemModal && (
        <AddItemModal
          isOpen={true}
          onClose={() => setShowAddItemModal(null)}
          onAdd={(item) => handleAddItem(showAddItemModal.vaultId, item)}
          type={showAddItemModal.type}
        />
      )}

      {showEditItemModal && (
        <EditItemModal
          isOpen={true}
          onClose={() => setShowEditItemModal(null)}
          onEdit={(itemId, item) => handleEditItem(showEditItemModal.vaultId, itemId, item)}
          item={showEditItemModal.item}
        />
      )}
    </div>
  );
}